import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { HOUSE_PACKS, HOUSE_UNIT_PRICE_CENTS } from '@greengrocer/shared';
import { apiFetch } from '../../lib/api';
import { formatAud, formatPack, formatPerUnit, formatUnits } from '../../lib/format';
import {
  Badge,
  Banner,
  Button,
  Card,
  Empty,
  Field,
  PageHeader,
  Spinner,
} from '../../components/ui';
import { IconPicker } from '../../components/IconPicker';

interface Store {
  id: string;
  name: string;
  canManage?: boolean;
  isActive?: boolean;
}
interface Product {
  id: string;
  name: string;
  emoji: string;
  description: string | null;
  isActive: boolean;
}
interface Pack {
  packUnits: number;
  packPriceCents: number;
}
interface Item {
  id: string;
  vegetableId: string;
  name: string;
  emoji: string;
  unitPriceCents: number;
  stockUnits: number;
  packs: Pack[];
  customerPriced: boolean;
  minPriceCents: number | null;
  maxPriceCents: number | null;
}

const centsFromInput = (value: string) => Math.round(Number.parseFloat(value || '0') * 100);

/*
 * A pack price is typed in and stored exactly as typed.
 *
 * The old form had to convert a chalkboard total ("1kg for $30") into the per-kg
 * rate the engine multiplied by, and that conversion was the single most
 * error-prone thing on this screen. Packs are flat totals in the database too,
 * so the conversion is gone rather than merely relocated — $150 for ten is
 * stored as 15000 against 10, and no rate is ever derived from it.
 */

export function Manage() {
  const queryClient = useQueryClient();
  const [storeId, setStoreId] = useState<string>('');
  const [editing, setEditing] = useState<string | null>(null);
  const [addingProduct, setAddingProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stores = useQuery({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/api/stores'),
  });
  const mine = stores.data?.filter((store) => store.canManage) ?? [];
  const active = storeId || mine[0]?.id || '';

  const products = useQuery({
    queryKey: ['vegetables'],
    queryFn: () => apiFetch<Product[]>('/api/vegetables'),
  });
  /*
   * Keyed 'staff-items', NOT 'items'.
   *
   * The same endpoint returns different shapes by role: customers get no
   * stockUnits, staff do. Sharing a key with the shop page meant that browsing
   * the shop first populated the cache with a customer-shaped response, so this
   * form read stockUnits as undefined, fell back to its default, and would have
   * written that placeholder over real stock on Save.
   */
  const items = useQuery({
    queryKey: ['staff-items', active],
    queryFn: () => apiFetch<Item[]>(`/api/stores/${active}/items`),
    enabled: Boolean(active),
  });

  const saveItem = useMutation({
    mutationFn: (input: { vegetableId: string; body: unknown }) =>
      apiFetch(`/api/stores/${active}/items/${input.vegetableId}`, {
        method: 'PUT',
        body: JSON.stringify(input.body),
      }),
    onSuccess: async () => {
      setEditing(null);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['staff-items', active] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const createProduct = useMutation({
    mutationFn: (body: { name: string; emoji: string; description?: string }) =>
      apiFetch<Product>('/api/vegetables', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: async () => {
      setError(null);
      setAddingProduct(false);
      await queryClient.invalidateQueries({ queryKey: ['vegetables'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const editProduct = useMutation({
    mutationFn: (input: { id: string; body: Record<string, unknown> }) =>
      apiFetch<Product>(`/api/vegetables/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify(input.body),
      }),
    onSuccess: async () => {
      setError(null);
      setEditingProduct(null);
      await queryClient.invalidateQueries({ queryKey: ['vegetables'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const removeItem = useMutation({
    mutationFn: (vegetableId: string) =>
      apiFetch(`/api/stores/${active}/items/${vegetableId}`, { method: 'DELETE' }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['staff-items', active] }),
    onError: (err: Error) => setError(err.message),
  });

  if (stores.isPending) return <Spinner label="Loading your stores" />;

  const activeStore = mine.find((store) => store.id === active);

  return (
    <div>
      <PageHeader
        title="Manage"
        subtitle="Set what each of your stores stocks, what it charges and which deals are running. You see only the stores you are assigned to."
        action={
          <Link to="/" className="sign text-[11px] text-leaf-700 hover:text-leaf-600">
            Add or remove stores →
          </Link>
        }
      />

      {error && (
        <div className="mt-5">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      {mine.length === 0 ? (
        <div className="mt-6">
          <Empty
            title="No stores assigned to you"
            body="Open one from the Shop page, or ask the owner to assign you to an existing store."
          />
        </div>
      ) : (
        <>
          {mine.length > 1 && (
            <div className="mt-6 flex flex-wrap gap-1">
              {mine.map((store) => (
                <button
                  key={store.id}
                  type="button"
                  onClick={() => setStoreId(store.id)}
                  className={`navpill ${store.id === active ? 'navpill-on' : 'navpill-off'}`}
                >
                  {store.name}
                  {store.isActive === false ? ' · closed' : ''}
                </button>
              ))}
            </div>
          )}

          <Card className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="sign text-[11px] text-ink-700">Product catalogue</h2>
                <p className="mt-1 max-w-xl text-[13px] text-ink-500">
                  Shared by every store. Add a product here, then set this store’s own price, stock
                  and deals against it below.
                </p>
              </div>
              <Button variant="quiet" onClick={() => setAddingProduct((open) => !open)}>
                {addingProduct ? 'Cancel' : 'New product'}
              </Button>
            </div>

            {addingProduct && (
              <ProductForm
                busy={createProduct.isPending}
                submitLabel="Add product"
                onSubmit={(body) => createProduct.mutate(body)}
              />
            )}
          </Card>

          {items.isPending ? (
            <Spinner label="Reading stock" />
          ) : (
            <div className="mt-5 space-y-3">
              <p className="sign text-[11px] text-ink-700">
                Pricing at {activeStore?.name ?? 'this store'}
              </p>

              {products.data
                ?.filter((product) => product.isActive)
                .map((product) => {
                  const item = items.data?.find((row) => row.vegetableId === product.id);
                  const open = editing === product.id;

                  return (
                    <div
                      key={product.id}
                      className={`tag p-4 ${item?.packs.length ? 'on-deal' : ''}`}
                    >
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <span className="medallion" aria-hidden>
                          {product.emoji}
                        </span>
                        <span className="text-[15px] text-ink-950">{product.name}</span>

                        {item ? (
                          <span className="tabular text-[13px] text-ink-700">
                            {item.customerPriced
                              ? 'customer sets the price'
                              : formatPerUnit(item.unitPriceCents)}{' '}
                            · {item.stockUnits ?? 0} on hand
                          </span>
                        ) : (
                          <Badge>Not stocked</Badge>
                        )}

                        {item && item.packs.length > 0 && (
                          <span className="ribbon">
                            {item.packs.length} pack{item.packs.length > 1 ? 's' : ''}
                          </span>
                        )}

                        <div className="ml-auto flex flex-wrap gap-2">
                          <Button
                            variant="quiet"
                            onClick={() =>
                              setEditingProduct(editingProduct === product.id ? null : product.id)
                            }
                          >
                            Edit product
                          </Button>
                          <Button
                            variant="quiet"
                            onClick={() => setEditing(open ? null : product.id)}
                          >
                            {open ? 'Close' : item ? 'Price & stock' : 'Stock it'}
                          </Button>
                          {item && (
                            <Button variant="quiet" onClick={() => removeItem.mutate(product.id)}>
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>

                      {editingProduct === product.id && (
                        <ProductForm
                          busy={editProduct.isPending}
                          submitLabel="Save product"
                          initial={{
                            name: product.name,
                            emoji: product.emoji,
                            description: product.description ?? '',
                          }}
                          onSubmit={(body) => editProduct.mutate({ id: product.id, body })}
                        />
                      )}

                      {open && (
                        <ItemForm
                          item={item}
                          busy={saveItem.isPending}
                          onSave={(body) => saveItem.mutate({ vegetableId: product.id, body })}
                        />
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Name, icon and description for a product in the shared catalogue.
 *
 * Separate from ItemForm on purpose: this edits the product itself, which every
 * store sees, while ItemForm edits one store's price and stock for it. Conflating
 * them would make "change the price" look like it might rename the product
 * everywhere.
 */
function ProductForm({
  busy,
  submitLabel,
  initial,
  onSubmit,
}: {
  busy: boolean;
  submitLabel: string;
  initial?: { name: string; emoji: string; description: string };
  onSubmit: (body: { name: string; emoji: string; description?: string }) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [emoji, setEmoji] = useState(initial?.emoji ?? '🥤');
  const [description, setDescription] = useState(initial?.description ?? '');

  return (
    <form
      className="mt-4 border-t hairline pt-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!name.trim()) return;
        onSubmit({
          name: name.trim(),
          emoji: emoji.trim() || '🥤',
          ...(description.trim() ? { description: description.trim() } : {}),
        });
      }}
    >
      <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
        <IconPicker value={emoji} onChange={setEmoji} />
        <div className="grid content-start gap-3">
          <Field
            label="Name"
            required
            minLength={2}
            placeholder="e.g. Lemon Squash"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Field
            label="Description"
            placeholder="Optional — how you would describe it on a sign"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
      </div>
      <Button type="submit" variant="primary" className="mt-4" disabled={busy}>
        {busy ? 'Saving…' : submitLabel}
      </Button>
    </form>
  );
}

/** Price, stock and quantity deals for one product in one store. */
function ItemForm({
  item,
  busy,
  onSave,
}: {
  item: Item | undefined;
  busy: boolean;
  onSave: (body: unknown) => void;
}) {
  const [price, setPrice] = useState(
    ((item?.unitPriceCents ?? HOUSE_UNIT_PRICE_CENTS) / 100).toFixed(2),
  );
  // No numeric fallback: a missing stock value must not quietly become a number
  // the admin then saves. New items start blank and the field is required.
  const [stock, setStock] = useState(item ? String(item.stockUnits) : '');
  const [packs, setPacks] = useState<{ units: string; total: string }[]>(
    item?.packs.length
      ? item.packs.map((pack) => ({
          units: String(pack.packUnits),
          total: (pack.packPriceCents / 100).toFixed(2),
        }))
      : [],
  );
  const [customerPriced, setCustomerPriced] = useState(item?.customerPriced ?? false);
  // ?? covers both null (not customer-priced) and undefined (brand new item).
  const [minPrice, setMinPrice] = useState(((item?.minPriceCents ?? 100) / 100).toFixed(2));
  const [maxPrice, setMaxPrice] = useState(((item?.maxPriceCents ?? 10000) / 100).toFixed(2));

  const unitCents = centsFromInput(price);
  const onHousePricing =
    unitCents === HOUSE_UNIT_PRICE_CENTS &&
    packs.length === HOUSE_PACKS.length &&
    HOUSE_PACKS.every(
      (house, index) =>
        Number.parseInt(packs[index]?.units ?? '0', 10) === house.packUnits &&
        centsFromInput(packs[index]?.total ?? '0') === house.packPriceCents,
    );

  return (
    <form
      className="mt-4 border-t hairline pt-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          unitPriceCents: unitCents,
          stockUnits: Number.parseInt(stock, 10),
          isActive: true,
          customerPriced,
          minPriceCents: customerPriced ? centsFromInput(minPrice) : null,
          maxPriceCents: customerPriced ? centsFromInput(maxPrice) : null,
          // A pack is a price the SHOP sets, so the two ideas cannot both hold
          // for one item. The server refuses the combination; this stops the
          // form from ever asking it to.
          packs: customerPriced
            ? []
            : packs
                .filter((pack) => pack.units && pack.total)
                .map((pack) => ({
                  packUnits: Number.parseInt(pack.units, 10),
                  // Straight through. Nothing is divided by the pack size on the
                  // way in or on the way out, which is what keeps the pack price
                  // genuinely independent of the single price.
                  packPriceCents: centsFromInput(pack.total),
                })),
        });
      }}
    >
      {/*
        The house terms in one tap. Stocking ten drinks by hand means typing the
        same two numbers twenty times.
      */}
      {/*
        "You name the price." The shop stops setting the price and starts
        setting only the range it will accept.
      */}
      <label className="well flex flex-wrap items-center gap-2.5 p-3">
        <input
          type="checkbox"
          checked={customerPriced}
          onChange={(event) => setCustomerPriced(event.target.checked)}
        />
        <span className="text-[13.5px] text-ink-900">Let the customer choose the price</span>
        <span className="text-[12.5px] text-ink-500">
          They type what they will pay, between the limits you set below.
        </span>
      </label>

      {customerPriced && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field
            label="Lowest you will accept (AUD)"
            type="number"
            step="0.01"
            min="0"
            required
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
          />
          <Field
            label="Highest you will accept (AUD)"
            type="number"
            step="0.01"
            min="0"
            required
            {...(centsFromInput(maxPrice) < centsFromInput(minPrice)
              ? { hint: 'The highest must not be below the lowest' }
              : {})}
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
          />
        </div>
      )}

      <div className={`flex flex-wrap items-center gap-3 ${customerPriced ? 'hidden' : 'mt-4'}`}>
        <Button
          type="button"
          variant="quiet"
          disabled={onHousePricing}
          onClick={() => {
            setPrice((HOUSE_UNIT_PRICE_CENTS / 100).toFixed(2));
            setPacks(
              HOUSE_PACKS.map((house) => ({
                units: String(house.packUnits),
                total: (house.packPriceCents / 100).toFixed(2),
              })),
            );
          }}
        >
          {onHousePricing ? 'On house pricing' : 'Use house pricing'}
        </Button>
        <span className="text-[13px] text-ink-500">
          {formatPerUnit(HOUSE_UNIT_PRICE_CENTS)}, and{' '}
          {HOUSE_PACKS.map((house) => formatPack(house.packUnits, house.packPriceCents)).join(', ')}{' '}
          as the deal.
        </span>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Field
          label={customerPriced ? 'Suggested price (AUD)' : 'Price for one (AUD)'}
          type="number"
          step="0.01"
          min="0"
          hint={
            customerPriced
              ? 'What the box opens on — the customer can change it'
              : 'What a single unit costs'
          }
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <Field
          label="Stock (units)"
          type="number"
          step="1"
          min="0"
          hint="Whole units on the shelf"
          required
          value={stock}
          onChange={(e) => setStock(e.target.value)}
        />
      </div>

      <div className={`mt-4 ${customerPriced ? 'hidden' : ''}`}>
        <p className="sign text-[11px] text-ink-700">Bulk packs</p>
        <p className="mt-1 max-w-xl text-[13px] text-ink-500">
          Set the pack price on its own — it is not a discount off the single price and nothing is
          worked out from it. If a basket holds more than one pack&apos;s worth, the cheapest mix of
          packs and singles is charged automatically.
        </p>

        <div className="mt-3 space-y-2">
          {packs.map((pack, index) => {
            const units = Number.parseInt(pack.units || '0', 10);
            const packCents = centsFromInput(pack.total);
            const singlesCents = units * unitCents;
            const ready = units > 1 && pack.total !== '';
            const dearer = ready && packCents >= singlesCents;
            const tooSmall = units === 1;

            return (
              <div key={index} className="well p-3">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="w-36">
                    <Field
                      label="Pack of"
                      type="number"
                      step="1"
                      min="2"
                      value={pack.units}
                      onChange={(event) =>
                        setPacks((rows) =>
                          rows.map((row, i) =>
                            i === index ? { ...row, units: event.target.value } : row,
                          ),
                        )
                      }
                    />
                  </div>
                  <div className="w-36">
                    <Field
                      label="Pack price (AUD)"
                      type="number"
                      step="0.01"
                      min="0"
                      value={pack.total}
                      onChange={(event) =>
                        setPacks((rows) =>
                          rows.map((row, i) =>
                            i === index ? { ...row, total: event.target.value } : row,
                          ),
                        )
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="quiet"
                    className="mb-0.5"
                    onClick={() => setPacks((rows) => rows.filter((_, i) => i !== index))}
                  >
                    Remove
                  </Button>
                </div>

                {/* Both numbers side by side, so "is this actually a deal?" is
                    answerable at a glance instead of by mental arithmetic. */}
                {ready && (
                  <p
                    className={`tabular mt-2 text-[12.5px] ${dearer ? 'text-tomato-600' : 'text-leaf-700'}`}
                  >
                    {formatUnits(units)} singly costs {formatAud(singlesCents)} → your pack{' '}
                    {formatAud(packCents)}
                    {dearer
                      ? ' — that is more, so it would never apply. Lower it below ' +
                        formatAud(singlesCents) +
                        '.'
                      : ` — saves ${formatAud(singlesCents - packCents)}`}
                  </p>
                )}
                {tooSmall && (
                  <p className="mt-2 text-[12.5px] text-tomato-600">
                    A pack of one would just be a second price for a single unit. Use two or more.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {packs.length < 5 && (
          <Button
            type="button"
            variant="quiet"
            className="mt-3"
            onClick={() => setPacks((rows) => [...rows, { units: '', total: '' }])}
          >
            Add a pack
          </Button>
        )}
      </div>

      <Button type="submit" variant="primary" className="mt-5" disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </Button>
    </form>
  );
}
